import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, isRetryableFailure } from "@/lib/api";
import { QUERY_KEYS } from "@/lib/constants";
import { invalidateSalesViews } from "@/lib/query";
import { enqueueOrder } from "@/lib/offline/queue";
import type {
  Order,
  CreateOrderRequest,
  UpdateOrderStatusRequest,
  PayOrderRequest,
  PaginatedResponse,
  OrderStatus,
} from "@/types";

interface OrderFilters {
  status?: OrderStatus | "active";
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  [key: string]: string | number | boolean | undefined;
}

export function useOrders(filters?: OrderFilters) {
  return useQuery({
    queryKey: QUERY_KEYS.orders(filters),
    queryFn: () => apiClient.get<PaginatedResponse<Order>>("/orders", filters),
  });
}

export function useActiveOrders() {
  return useQuery({
    queryKey: QUERY_KEYS.activeOrders,
    queryFn: () =>
      apiClient.get<PaginatedResponse<Order>>("/orders", {
        status: "active",
        limit: 100,
      }),
    refetchInterval: 10000, // Refresh every 10 seconds
  });
}

export interface OrderHistoryQuery {
  startDate?: string;
  endDate?: string;
  shiftId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export function orderHistoryQuery(filters?: OrderHistoryQuery) {
  return {
    ...(filters?.shiftId ? {} : { status: "completed" }),
    shiftId: filters?.shiftId,
    search: filters?.search || undefined,
    page: filters?.page,
    limit: filters?.limit,
    from: filters?.startDate,
    to: filters?.endDate,
  };
}

export function useOrderHistory(filters?: OrderHistoryQuery) {
  return useQuery({
    queryKey: ["orders", "history", filters],
    queryFn: () =>
      apiClient.get<PaginatedResponse<Order>>(
        "/orders",
        orderHistoryQuery(filters),
      ),
    placeholderData: (prev) => prev,
  });
}

export function useOrder(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.order(id),
    queryFn: () => apiClient.get<Order>(`/orders/${id}`),
    enabled: !!id,
  });
}

export function useOrderInvoice(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.orderInvoice(id),
    queryFn: () => apiClient.get<Order>(`/orders/${id}/invoice`),
    enabled: !!id,
  });
}

export type CreateOrderResult =
  | { status: "synced"; order: Order; idempotencyKey: string }
  | { status: "queued"; idempotencyKey: string };

function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export function useCreateOrderOffline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: CreateOrderRequest,
    ): Promise<CreateOrderResult> => {
      const idempotencyKey = newIdempotencyKey();
      try {
        const order = await apiClient.post<Order>("/orders", data, {
          idempotencyKey,
        });

        return { status: "synced", order, idempotencyKey };
      } catch (err) {
        if (isRetryableFailure(err)) {
          await enqueueOrder({ idempotencyKey, payload: data });

          return { status: "queued", idempotencyKey };
        }

        throw err;
      }
    },
    onSuccess: (result) => {
      if (result.status === "synced") {
        invalidateSalesViews(queryClient);
      }
    },
  });
}

export function useUpdateOrderStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateOrderStatusRequest;
    }) => apiClient.patch<Order>(`/orders/${id}/status`, data),
    onSuccess: (_, { id }) => {
      invalidateSalesViews(queryClient);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.order(id) });
    },
  });
}

export function usePayOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PayOrderRequest }) =>
      apiClient.post<Order>(`/orders/${id}/pay`, data),
    onSuccess: (_, { id }) => {
      invalidateSalesViews(queryClient);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.order(id) });
    },
  });
}
